/**
 * `UcpDiscountCodesEditor` — the "Discount codes" part of the UCP
 * checkout row (ADR-0254). The codes themselves live in the merchant's
 * Stripe account (promotion codes); this only decides which of them a
 * shopping agent may use: none (default), all active ones, or a short
 * list. Nothing is saved until the merchant presses Save.
 */

import { useState } from 'react';

import type { UcpDiscountMode, UcpDiscountSetting } from '../../api/site-ucp.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

const CODE_RE = /^[A-Z0-9_-]{1,64}$/;
const MAX_CODES = 50;

/** Text area → upper-cased, de-duplicated codes; an error when one is not a valid code. */
export function parseCodeList(text: string): { codes: string[] } | { error: string } {
  const codes: string[] = [];
  for (const raw of text.split(/[,\s]+/)) {
    const c = raw.trim().toUpperCase();
    if (c.length === 0) continue;
    if (!CODE_RE.test(c)) return { error: `"${raw.trim()}" is not a valid code. Use letters, digits, - and _ only.` };
    if (!codes.includes(c)) codes.push(c);
  }
  if (codes.length > MAX_CODES) return { error: `At most ${MAX_CODES} codes.` };
  return { codes };
}

const OPTIONS: readonly { mode: UcpDiscountMode; title: string; body: string }[] = [
  { mode: 'off', title: 'Off', body: 'Agents can’t use discount codes. Orders are charged at full price.' },
  { mode: 'all', title: 'All my active Stripe promotion codes', body: 'Any code that works in your Stripe account works for agents too, with its expiry, minimum order and usage limit.' },
  { mode: 'allow_list', title: 'Only these codes', body: 'Agents can use only the codes you list. Each must also be an active promotion code in Stripe.' },
];

export function UcpDiscountCodesEditor({ initial, saving, onSave }: { initial: UcpDiscountSetting; saving: boolean; onSave: (s: UcpDiscountSetting) => void }): JSX.Element {
  const C = useC();
  const [mode, setMode] = useState<UcpDiscountMode>(initial.mode);
  const [text, setText] = useState(initial.allow_list.join(', '));
  const [error, setError] = useState<string | null>(null);

  const save = (): void => {
    if (mode !== 'allow_list') {
      setError(null);
      onSave({ mode, allow_list: [] });
      return;
    }
    const parsed = parseCodeList(text);
    if ('error' in parsed) return setError(parsed.error);
    if (parsed.codes.length === 0) return setError('Add at least one code, or choose another option.');
    setError(null);
    onSave({ mode, allow_list: parsed.codes });
  };

  return (
    <div className="space-y-3 pt-2" data-testid="ucp-discount-editor">
      <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
        Your discount codes stay in Stripe — create and edit them there. Sill checks each code with Stripe when an agent applies it and again before charging, takes it off the items (not shipping), and charges the reduced total.
      </div>
      <div role="radiogroup" aria-label="Agent discount codes" className="space-y-2">
        {OPTIONS.map((o) => (
          <label key={o.mode} className="flex gap-2 p-2.5" style={{ background: mode === o.mode ? C.surface2 : 'transparent', border: `1px solid ${C.border}`, borderRadius: 2, cursor: 'pointer' }}>
            <input type="radio" name="ucp-discount-mode" value={o.mode} checked={mode === o.mode} onChange={() => setMode(o.mode)} data-testid={`ucp-discount-mode-${o.mode}`} />
            <span>
              <span style={{ display: 'block', fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 500 }}>{o.title}</span>
              <span style={{ display: 'block', fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>{o.body}</span>
            </span>
          </label>
        ))}
      </div>
      {mode === 'allow_list' ? (
        <label style={{ display: 'block' }}>
          <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1.5, marginBottom: 4 }}>CODES AGENTS MAY USE</div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder="AGENT10, WELCOME5"
            data-testid="ucp-discount-allow-list"
            style={{ width: '100%', padding: '8px 10px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 2, color: C.text, fontFamily: monoFont, fontSize: 12 }}
          />
        </label>
      ) : null}
      {error !== null ? <div role="alert" style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg }}>{error}</div> : null}
      <button
        type="button"
        disabled={saving}
        onClick={save}
        data-testid="ucp-discount-save"
        className="px-3 py-2"
        style={{ background: C.lime, color: '#0B0B0B', border: 'none', borderRadius: 2, fontFamily: sansFont, fontSize: 12, fontWeight: 500, opacity: saving ? 0.6 : 1 }}
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}
